import { IconButton } from '@mui/material'
import { useAtomValue } from 'jotai'
import type { FC } from 'react'
import { Suspense, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import IcBaselineClose from '~icons/ic/baseline-close'
import LoadingPage from 'src/components/LoadingPage'
import { UIDialog } from 'src/components/ui/Modal'
import type {
  GenerationModelBaseFragment,
  GenerationModelDetailFragment,
  GenerationModelVersionDetailFragment,
} from 'src/services/apollo/generated/graphql'
import { parseModelExtras } from 'src/services/helpers/modelApiParamsPacker'
import { localeAtom } from 'src/stores/locale'
import { getLocalizedTackName } from 'src/utils/tack'
import ModelEditContent from './ModelEditContent'
import { ModelVersionUpsertDialogContent } from './ModelSubmitForms/ModelAssetForm'
import ModelSubmitFormProvider from './ModelSubmitForms/ModelSubmitFormProvider'

type ModelEditFormProps = {
  mutate?: () => any
  onSuccess?: () => any
  onClose?: () => any
}

type ModelEditDialogContentProps = {
  model: GenerationModelDetailFragment
  appendCompForInfo?: JSX.Element
  highlightId?: string
} & ModelEditFormProps

const ModelEditDialogContent: FC<ModelEditDialogContentProps> = ({
  onClose,
  model,
  ...submitFormProps
}) => {
  const { t } = useTranslation(['market'])
  const locale = useAtomValue(localeAtom)

  const initialTags = useMemo(() => {
    if (model.tacks && model.tacks.length > 0) {
      return model.tacks.map(tack => ({
        id: tack.id,
        name: tack.codeName,
        displayName: getLocalizedTackName(
          tack.tackTerms,
          tack.defaultName,
          locale,
        ),
        mediaId: tack.mediaId ?? undefined,
      }))
    }
    return []
  }, [model.tacks, locale])

  // remove mediaId, extra, tags, tacks; use processed versions instead
  const {
    mediaId,
    extra,
    tags: _tags,
    tacks: _tacks,
    loraBaseModelTypes: _loraBaseModelTypes,
    ...restModel
  } = model

  const initialValues = useMemo(() => {
    return {
      ...restModel,
      extra: parseModelExtras(extra),
      modelId: model.id,
      media: model.media,
      editFilename: model.latestAvailableVersion?.fileUploadRecord?.filename,
      tags: initialTags,
      loraBaseModelTypes: model.loraBaseModelTypes ?? [],
    }
  }, [
    restModel,
    extra,
    model.id,
    model.media,
    model.latestAvailableVersion?.fileUploadRecord?.filename,
    initialTags,
    model.loraBaseModelTypes,
  ])

  return (
    <Suspense fallback={<LoadingPage />}>
      <section className="px-8 pt-6 text-2xl flex justify-between items-center">
        <h2 className="font-bold">
          {t('market:edit-model-form.dialog-title')}
        </h2>
        <IconButton edge="end" onClick={onClose}>
          <IcBaselineClose className="size-6" />
        </IconButton>
      </section>
      <ModelSubmitFormProvider key={model.id} initialValues={initialValues}>
        <ModelEditContent onClose={onClose} {...submitFormProps} />
      </ModelSubmitFormProvider>
    </Suspense>
  )
}

export const dialogEditModel = (props: ModelEditDialogContentProps) => {
  return UIDialog.create({
    children: ({ close }) => (
      <ModelEditDialogContent onClose={() => close()} {...props} />
    ),
    className: 'w-[calc(100vw-64px)] max-w-screen-mlg',
  })
}

export const dialogEditModelVersion = (
  model: GenerationModelDetailFragment,
  versionData: GenerationModelVersionDetailFragment,
  props?: ModelEditFormProps,
) => {
  return UIDialog.create({
    children: ({ close }) => (
      <ModelVersionUpsertDialogContent
        modelProps={{ ...model }}
        versionData={versionData}
        onClose={() => close()}
        mutate={props?.mutate}
      />
    ),
    className: 'w-[calc(100vw-64px)] max-w-screen-mlg',
  })
}

export const dialogAddModelVersions = (
  model: Pick<
    GenerationModelBaseFragment,
    'id' | 'type' | 'isPrivate' | 'visibilityType'
  >,
  props?: ModelEditFormProps,
) => {
  return UIDialog.create({
    children: ({ close }) => (
      <ModelVersionUpsertDialogContent
        modelProps={model}
        mutate={props?.mutate}
        onClose={() => close()}
      />
    ),
    className: 'w-[calc(100vw-64px)] max-w-screen-mlg',
  })
}
